import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CATEGORIES = [
  { key: 'tous', label: '📋 Tous', color: '#6C63FF' },
  { key: 'sante', label: '❤️ Santé', color: '#F44336' },
  { key: 'vetements', label: '👕 Vêtements', color: '#E91E63' },
  { key: 'document', label: '📄 Documents', color: '#FF9800' },
  { key: 'preference', label: '💚 Préférences', color: '#4CAF50' },
  { key: 'admin', label: '🏛️ Admin', color: '#2196F3' },
  { key: 'autre', label: '🔮 Autre', color: '#9C27B0' },
];

const CAT_EMOJIS = { sante: '❤️', vetements: '👕', document: '📄', preference: '💚', admin: '🏛️', autre: '🔮' };

export default function Memory() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [catFilter, setCatFilter] = useState('tous');
  const [search, setSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    category: 'autre', title: '', content: '', related_user_id: '', tags: ''
  });

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (catFilter !== 'tous') params.set('category', catFilter);
    if (memberFilter) params.set('user_id', memberFilter);
    if (search) params.set('search', search);
    api.get(`/memory?${params}`).then(r => setItems(r.data)).catch(console.error);
    api.get('/family/members').then(r => setMembers(r.data)).catch(console.error);
  }, [catFilter, memberFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ category: catFilter !== 'tous' ? catFilter : 'autre', title: '', content: '', related_user_id: '', tags: '' });
    setEditItem(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({ category: item.category, title: item.title, content: item.content || '', related_user_id: item.related_user_id || '', tags: item.tags || '' });
    setEditItem(item);
    setSelected(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, related_user_id: form.related_user_id || null };
      if (editItem) await api.put(`/memory/${editItem.id}`, payload);
      else await api.post('/memory', payload);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editItem || !window.confirm('Supprimer cette information ?')) return;
    await api.delete(`/memory/${editItem.id}`);
    setShowForm(false);
    load();
  };

  const catColor = (cat) => CATEGORIES.find(c => c.key === cat)?.color || '#6C63FF';

  const quickItems = [
    { title: '💊 Médicament', cat: 'sante', placeholder: 'Nom + posologie + personne concernée' },
    { title: '📏 Taille vêtements', cat: 'vetements', placeholder: 'Taille hauts / bas / chaussures' },
    { title: '💉 Vaccin', cat: 'sante', placeholder: 'Vaccin + date + rappel prévu' },
    { title: '📋 Papier école', cat: 'document', placeholder: 'Référence + où le trouver' },
    { title: '🍕 Préférence repas', cat: 'preference', placeholder: 'Aime / n\'aime pas + allergies' },
    { title: '🔑 Code / Accès', cat: 'admin', placeholder: 'Service + identifiant (attention sécurité !)' },
  ];

  return (
    <div>
      <div className="topbar">
        <div className="page-title">🧠 Mémoire</div>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Ajouter</button>
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        {/* RECHERCHE */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input className="form-input" placeholder="🔍 Rechercher... (vaccin, taille, code...)" value={search}
            onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 14 }} />
        </div>

        {/* FILTRES CATÉGORIES */}
        <div className="cat-filter">
          {CATEGORIES.map(c => (
            <button key={c.key} className={`cat-chip${catFilter === c.key ? ' active' : ''}`}
              onClick={() => setCatFilter(c.key)}
              style={catFilter === c.key ? { borderColor: c.color, background: c.color + '18', color: c.color } : {}}>
              {c.label}
            </button>
          ))}
        </div>

        {/* FILTRE MEMBRE */}
        {members.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }}>
            <button className={`cat-chip${!memberFilter ? ' active' : ''}`} onClick={() => setMemberFilter('')}>👨‍👩‍👧 Famille</button>
            {members.map(m => (
              <button key={m.id} className={`cat-chip${memberFilter == m.id ? ' active' : ''}`}
                onClick={() => setMemberFilter(memberFilter == m.id ? '' : m.id)}
                style={memberFilter == m.id ? { borderColor: m.color, background: m.color + '18', color: m.color } : {}}>
                {m.avatar_emoji} {m.name}
              </button>
            ))}
          </div>
        )}

        {/* RACCOURCIS AJOUT RAPIDE */}
        {items.length === 0 && !search && catFilter === 'tous' && (
          <>
            <div className="section-title">💡 Suggestions pour commencer</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {quickItems.map((qi, i) => (
                <button key={i} onClick={() => {
                  setForm({ category: qi.cat, title: qi.title.replace(/^[^\s]+\s/, ''), content: '', related_user_id: '', tags: '' });
                  setEditItem(null);
                  setShowForm(true);
                }} style={{ background: 'white', border: '2px dashed #E0E4F0', borderRadius: 12, padding: '12px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#2C2C2C', marginBottom: 4 }}>{qi.title}</div>
                  <div style={{ fontSize: 11, color: '#8888AA', lineHeight: 1.3 }}>{qi.placeholder}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* LISTE */}
        {items.map(item => (
          <div key={item.id} className={`memory-card ${item.category}`} onClick={() => setSelected(selected?.id === item.id ? null : item)}>
            <div className="flex-between" style={{ marginBottom: 4 }}>
              <div className="memory-title">{CAT_EMOJIS[item.category] || '📌'} {item.title}</div>
              <button onClick={e => { e.stopPropagation(); openEdit(item); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#BBBBCC', padding: 4 }}>✏️</button>
            </div>

            {item.content && (
              <div className="memory-content" style={{ maxHeight: selected?.id === item.id ? 'none' : '2.8em', overflow: 'hidden', WebkitLineClamp: selected?.id === item.id ? 'none' : 2, display: '-webkit-box', WebkitBoxOrient: 'vertical' }}>
                {item.content}
              </div>
            )}

            <div className="memory-footer">
              <div className="memory-tags">
                {item.related_user_name && (
                  <span className="memory-tag" style={{ background: members.find(m => m.name === item.related_user_name)?.color + '22', color: members.find(m => m.name === item.related_user_name)?.color }}>
                    {members.find(m => m.name === item.related_user_name)?.avatar_emoji} {item.related_user_name}
                  </span>
                )}
                {item.tags && item.tags.split(',').map(t => t.trim()).filter(Boolean).map((tag, i) => (
                  <span key={i} className="memory-tag">{tag}</span>
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#AAAACC', whiteSpace: 'nowrap' }}>
                {new Date(item.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </div>
            </div>

            {selected?.id === item.id && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #F0F2FF', display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); openEdit(item); }} style={{ flex: 1 }}>✏️ Modifier</button>
                <button className="btn btn-danger btn-sm" onClick={async e => { e.stopPropagation(); if (window.confirm('Supprimer ?')) { await api.delete(`/memory/${item.id}`); setSelected(null); load(); } }} style={{ flex: 1 }}>🗑️ Supprimer</button>
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && (search || catFilter !== 'tous' || memberFilter) && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <div className="empty-text">Aucun résultat trouvé</div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => { setSearch(''); setCatFilter('tous'); setMemberFilter(''); }}>Effacer les filtres</button>
          </div>
        )}
      </div>

      {/* MODAL FORMULAIRE */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editItem ? '✏️ Modifier' : '🧠 Nouvelle info'}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.filter(c => c.key !== 'tous').map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Concerne</label>
                <select className="form-input" value={form.related_user_id} onChange={e => setForm({ ...form, related_user_id: e.target.value })}>
                  <option value="">Toute la famille</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.avatar_emoji} {m.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Titre</label>
              <input className="form-input" placeholder="Ex: Allergie aux arachides, Taille chaussures, Code wifi..." value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
            </div>

            <div className="form-group">
              <label className="form-label">Informations</label>
              <textarea className="form-input" placeholder="Détails, numéros, dates importantes, instructions..." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} />
            </div>

            <div className="form-group">
              <label className="form-label">Mots-clés (séparés par des virgules)</label>
              <input className="form-input" placeholder="Ex: allergie, médecin, urgent..." value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {editItem && <button className="btn btn-danger btn-sm" onClick={handleDelete}>🗑️</button>}
              <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
